import { useQuery, useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2, Save, Building2 } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { z } from "zod";
import { useEffect } from "react";
import type { Security, SecurityValuation, SecurityEncumbrance } from "@shared/schema";

const securityEditSchema = z.object({
  securityType: z.enum([
    "residential_property",
    "commercial_property",
    "vehicle",
    "equipment",
    "financial_asset",
  ]),
  status: z.enum([
    "identified",
    "proposed",
    "valued",
    "approved",
    "registered",
    "varied",
    "substituted",
    "discharged",
    "cancelled",
  ]),
  estimatedValue: z.string().min(1, "Value is required"),
  description: z.string().optional().nullable(),
  notes: z.string().optional().nullable(),
});

type SecurityEditFormData = z.infer<typeof securityEditSchema>;

interface SecurityDetailDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  securityId: string | null;
  facilityId: string | null;
}

const SECURITY_TYPE_OPTIONS = [
  { value: "residential_property", label: "Residential Property" },
  { value: "commercial_property", label: "Commercial Property" },
  { value: "vehicle", label: "Vehicle" },
  { value: "equipment", label: "Equipment" },
  { value: "financial_asset", label: "Financial Asset" },
];

const STATUS_OPTIONS = [
  { value: "identified", label: "Identified" },
  { value: "proposed", label: "Proposed" },
  { value: "valued", label: "Valued" },
  { value: "approved", label: "Approved" },
  { value: "registered", label: "Registered" },
  { value: "varied", label: "Varied" },
  { value: "substituted", label: "Substituted" },
  { value: "discharged", label: "Discharged" },
  { value: "cancelled", label: "Cancelled" },
];

export function SecurityDetailDrawer({
  open,
  onOpenChange,
  securityId,
  facilityId,
}: SecurityDetailDrawerProps) {
  const { toast } = useToast();

  // Fetch security details
  const { data: security, isLoading } = useQuery<Security>({
    queryKey: [`/api/crm/securities/${securityId}`],
    enabled: !!securityId && open,
  });

  // Fetch valuations for this security
  const { data: valuations = [] } = useQuery<SecurityValuation[]>({
    queryKey: [`/api/crm/securities/${securityId}/valuations`],
    enabled: !!securityId && open,
  });

  // Fetch encumbrances for this security
  const { data: encumbrances = [] } = useQuery<SecurityEncumbrance[]>({
    queryKey: [`/api/crm/securities/${securityId}/encumbrances`],
    enabled: !!securityId && open,
  });

  const form = useForm<SecurityEditFormData>({
    resolver: zodResolver(securityEditSchema),
    defaultValues: {
      securityType: "residential_property",
      status: "identified",
      estimatedValue: "",
      description: "",
      notes: "",
    },
  });

  // Update form when security data loads
  useEffect(() => {
    if (security) {
      form.reset({
        securityType: security.securityType as SecurityEditFormData["securityType"],
        status: (security.status ?? "identified") as SecurityEditFormData["status"],
        estimatedValue: security.estimatedValue?.toString() ?? "",
        description: security.description ?? "",
        notes: security.notes ?? "",
      });
    }
  }, [security, form]);

  const updateMutation = useMutation({
    mutationFn: async (data: SecurityEditFormData) => {
      const response = await apiRequest("PATCH", `/api/crm/securities/${securityId}`, {
        ...data,
        estimatedValue: data.estimatedValue,
      });
      if (!response.ok) {
        const error = await response.text();
        throw new Error(error || "Failed to update security");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/crm/securities/${securityId}`] });
      if (facilityId) {
        queryClient.invalidateQueries({
          queryKey: [`/api/crm/facilities/${facilityId}/securities`],
        });
        queryClient.invalidateQueries({
          queryKey: [`/api/crm/facilities/${facilityId}/securities/summary`],
        });
      }
      toast({
        title: "Success",
        description: "Security updated successfully",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: SecurityEditFormData) => {
    updateMutation.mutate(data);
  };

  // Format currency
  const formatCurrency = (value: string | number | null | undefined): string => {
    if (value === null || value === undefined) return "-";
    const num = typeof value === "string" ? parseFloat(value) : value;
    if (isNaN(num)) return "-";
    return new Intl.NumberFormat("en-AU", {
      style: "currency",
      currency: "AUD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(num);
  };

  // Format date
  const formatDate = (date: string | Date | null | undefined): string => {
    if (!date) return "-";
    return new Date(date).toLocaleDateString("en-AU", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  if (!securityId) return null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-[600px] overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            Security Details
          </SheetTitle>
          <SheetDescription>
            View and edit security information
          </SheetDescription>
        </SheetHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : security ? (
          <Tabs defaultValue="details" className="mt-4">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="details">Details</TabsTrigger>
              <TabsTrigger value="valuations">
                Valuations ({valuations.length})
              </TabsTrigger>
              <TabsTrigger value="encumbrances">
                Encumbrances ({encumbrances.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="details" className="space-y-4 mt-4">
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                  <FormField
                    control={form.control}
                    name="securityType"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Security Type</FormLabel>
                        <Select
                          onValueChange={field.onChange}
                          value={field.value}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select type" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {SECURITY_TYPE_OPTIONS.map((option) => (
                              <SelectItem key={option.value} value={option.value}>
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="status"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Status</FormLabel>
                        <Select
                          onValueChange={field.onChange}
                          value={field.value}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select status" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {STATUS_OPTIONS.map((option) => (
                              <SelectItem key={option.value} value={option.value}>
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="estimatedValue"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Estimated Value (AUD)</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            placeholder="0.00"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="description"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Description</FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder="Security description..."
                            className="min-h-[80px]"
                            {...field}
                            value={field.value ?? ""}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="notes"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Notes</FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder="Additional notes..."
                            className="min-h-[60px]"
                            {...field}
                            value={field.value ?? ""}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <Separator />

                  <div className="flex justify-end">
                    <Button type="submit" disabled={updateMutation.isPending}>
                      {updateMutation.isPending ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <Save className="mr-2 h-4 w-4" />
                      )}
                      Save Changes
                    </Button>
                  </div>
                </form>
              </Form>
            </TabsContent>

            <TabsContent value="valuations" className="space-y-4 mt-4">
              {valuations.length === 0 ? (
                <Card>
                  <CardContent className="pt-6">
                    <p className="text-sm text-muted-foreground text-center">
                      No valuations recorded for this security.
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-3">
                  {valuations.map((valuation) => (
                    <Card key={valuation.id}>
                      <CardHeader className="py-3">
                        <div className="flex justify-between items-start">
                          <div>
                            <CardTitle className="text-sm">
                              {formatCurrency(valuation.valuationAmount)}
                            </CardTitle>
                            <p className="text-xs text-muted-foreground">
                              {formatDate(valuation.valuationDate)}
                            </p>
                          </div>
                          <Badge variant="outline" className="text-xs capitalize">
                            {valuation.valuationType?.replace("_", " ") ?? "N/A"}
                          </Badge>
                        </div>
                      </CardHeader>
                      <CardContent className="pt-0 pb-3">
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          <div>
                            <span className="text-muted-foreground">Valuer:</span>
                            <span className="ml-1">{valuation.valuerName ?? "-"}</span>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Current:</span>
                            <span className="ml-1">
                              {valuation.isCurrent ? "Yes" : "No"}
                            </span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="encumbrances" className="space-y-4 mt-4">
              {encumbrances.length === 0 ? (
                <Card>
                  <CardContent className="pt-6">
                    <p className="text-sm text-muted-foreground text-center">
                      No encumbrances recorded for this security.
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-3">
                  {encumbrances.map((encumbrance) => (
                    <Card key={encumbrance.id}>
                      <CardHeader className="py-3">
                        <div className="flex justify-between items-start">
                          <div>
                            <CardTitle className="text-sm">
                              {encumbrance.holderName ?? "Unknown Holder"}
                            </CardTitle>
                            <p className="text-xs text-muted-foreground capitalize">
                              {encumbrance.encumbranceType?.replace("_", " ") ?? "N/A"}
                            </p>
                          </div>
                          <Badge
                            variant={
                              encumbrance.dischargeRequired
                                ? "destructive"
                                : "default"
                            }
                            className="text-xs capitalize"
                          >
                            {encumbrance.dischargeRequired ? "Discharge Required" : "Active"}
                          </Badge>
                        </div>
                      </CardHeader>
                      <CardContent className="pt-0 pb-3">
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          <div>
                            <span className="text-muted-foreground">Balance:</span>
                            <span className="ml-1">
                              {formatCurrency(encumbrance.currentBalance)}
                            </span>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Priority:</span>
                            <span className="ml-1">
                              {encumbrance.priorityPosition ?? "-"}
                            </span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        ) : (
          <div className="flex items-center justify-center py-8">
            <p className="text-sm text-muted-foreground">Security not found.</p>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
